import { atom } from 'jotai'
import { atomWithStorage, createJSONStorage } from 'jotai/utils'
import { SyncStorage } from 'jotai/vanilla/utils/atomWithStorage'

import { PortionCount } from '@mindfulchefuk/config/portionCountConfig'
import { TFoodGroup, TMealPlan } from '@mindfulchefuk/config/mealPlanConfig'
import {
  TSummaryAvailabilityRecipe,
  TRecipeAvailability,
} from '@mindfulchefuk/types/recipeAvailabilities'
import { RecipeSortId } from '@mindfulchefuk/config/sortOptionsConfig'
import { TCreateOnboardingPreviewResponse } from '@mindfulchefuk/onboarding/services/postOnboardingPreview/createOnboardingPreview'

const storage: SyncStorage<{ [recipeId: string]: number }> = createJSONStorage(
  () => sessionStorage
)
export const voucherCodeAtom = atomWithStorage('voucherCode', 'NEW25X4')
export const portionCountAtom = atomWithStorage<PortionCount>('portionCount', 2)
export const mealPlanAtom = atomWithStorage<TMealPlan>('mealPlan', 'Balanced')
export const deliveryDateAtom = atomWithStorage<string>('deliveryDate', '')
export const excludedFoodGroupAtom = atomWithStorage<TFoodGroup[]>(
  'excludedFoodGroups',
  []
)
export const selectedSortByAtom = atom<RecipeSortId>('customerFav')
export const recipeAvailabilitiesAtom = atomWithStorage<
  TRecipeAvailability<TSummaryAvailabilityRecipe>[]
>('recipeAvailabilities', [])
export const recipesInBasketAtom = atomWithStorage<{
  [recipeId: string]: number
}>('recipesInBasket', {}, storage)
export const onboardingOrderPreviewAtom =
  atom<TCreateOnboardingPreviewResponse>()
export const leadContactAtom = atomWithStorage<{
  leadContactId: number
  accessToken: string
}>('leadContact', null)

export const updateChooseRecipeFiltersAtom = atom(
  null,
  (
    get,
    set,
    update: {
      portionCount?: PortionCount
      mealPlan?: TMealPlan
      deliveryDate?: string
      excludedFoodGroups?: TFoodGroup[]
    }
  ) => {
    const exisitingPortionCountValue = get(portionCountAtom)
    const exisitingMealPlanAtom = get(mealPlanAtom)
    const exisitingDeliveryDateValue = get(deliveryDateAtom)
    const selectedExcludedFoodGroups = get(excludedFoodGroupAtom)

    set(portionCountAtom, update.portionCount ?? exisitingPortionCountValue)
    set(mealPlanAtom, update.mealPlan ?? exisitingMealPlanAtom)
    set(deliveryDateAtom, update.deliveryDate ?? exisitingDeliveryDateValue)
    set(
      excludedFoodGroupAtom,
      update.excludedFoodGroups ?? selectedExcludedFoodGroups
    )
  }
)

export const updateChooseRecipeSortingAtom = atom(
  null,
  (get, set, update: RecipeSortId) => {
    const recipeAvailabilities = get(recipeAvailabilitiesAtom)

    const sortedRecipes = recipeAvailabilities.sort((recipeA, recipeB) => {
      if (update === 'customerFav') {
        return (
          recipeA.recipeSortingOptions.sortOrder -
          recipeB.recipeSortingOptions.sortOrder
        )
      }

      return (
        recipeA.recipeSortingOptions[update] -
        recipeB.recipeSortingOptions[update]
      )
    })

    set(recipeAvailabilitiesAtom, sortedRecipes)
    set(selectedSortByAtom, update)
  }
)

export const addRecipeToBasketAtom = atom(null, (get, set, update: string) => {
  const recipesInBasket = get(recipesInBasketAtom)

  const recipeIdKeys = Object.keys(recipesInBasket)

  if (!recipeIdKeys.includes(update)) {
    set(recipesInBasketAtom, { ...recipesInBasket, [update]: 1 })
  } else {
    const selectedRecipeQuantity = recipesInBasket[update]
    set(recipesInBasketAtom, {
      ...recipesInBasket,
      [update]: selectedRecipeQuantity + 1,
    })
  }
})

export const removeRecipeFromBasketAtom = atom(
  null,
  (get, set, update: string) => {
    const recipesInBasket = get(recipesInBasketAtom)
    const isRecipeQuantityAboveOne = recipesInBasket[update] > 1

    if (isRecipeQuantityAboveOne) {
      set(recipesInBasketAtom, {
        ...recipesInBasket,
        [update]: recipesInBasket[update] - 1,
      })
    } else {
      delete recipesInBasket[update]
      set(recipesInBasketAtom, { ...recipesInBasket })
    }
  }
)

export const recipeContentsAtom = atom((get) => {
  const recipesInBasket = get(recipesInBasketAtom)
  const portionCount = get(portionCountAtom)

  return Object.entries(recipesInBasket).flatMap((recipeIdAndQuantity) => {
    const [recipeId, recipeQuantity] = recipeIdAndQuantity

    return new Array(recipeQuantity).fill({
      recipe_id: recipeId,
      portions: portionCount,
    })
  })
})

export const selectedRecipeIdsAtom = atom((get) => {
  const recipesInBasket = get(recipesInBasketAtom)

  return Object.entries(recipesInBasket).flatMap((recipeIdAndQuantity) => {
    const [recipeId, recipeQuantity] = recipeIdAndQuantity

    return new Array(recipeQuantity).fill(parseInt(recipeId, 10))
  })
})

export const onboardingValueAtom = atom((get) => {
  const recipeIds = get(selectedRecipeIdsAtom)
  const portionCount = get(portionCountAtom)
  const selectedMealPlan = get(mealPlanAtom)
  const savedVoucherCode = get(voucherCodeAtom)
  const selectedDeliveryDate = get(deliveryDateAtom)
  const excludedFoodGroups = get(excludedFoodGroupAtom)

  return {
    itemIds: recipeIds,
    portionCount,
    selectedMealPlan,
    savedVoucherCode,
    selectedDeliveryDate,
    excludedFoodGroups,
  }
})
